/**
 * Markdown (GFM) to a flat block model the PDF renderer can lay out.
 *
 * Parsing reuses the mdast toolchain that already ships with react-markdown, so
 * the PDF sees the same document structure the screen does, minus the things a
 * printed page cannot carry (remote images, raw HTML).
 */

import fromMarkdown from 'mdast-util-from-markdown';
import gfmSyntax from 'micromark-extension-gfm';
import { fromMarkdown as gfmFromMarkdown } from 'mdast-util-gfm';
import { PdfBlock, PdfLink, PdfListItem, PdfTableCell, PdfTextRun } from './types';
import { sanitizeForPdf } from './text';

/** Loose mdast node — the shipped typings predate several GFM node types. */
interface MdNode {
  type: string;
  value?: string;
  depth?: number;
  ordered?: boolean;
  start?: number | null;
  checked?: boolean | null;
  lang?: string | null;
  url?: string;
  alt?: string | null;
  children?: MdNode[];
}

interface InlineContext {
  /** Collects links in document order so the caller can footnote them. */
  links: PdfLink[];
  linkFootnotes: boolean;
  seen: Record<string, number>;
}

export interface MarkdownDocument {
  blocks: PdfBlock[];
  links: PdfLink[];
}

type RunStyle = Pick<PdfTextRun, 'bold' | 'italic' | 'code' | 'strike' | 'href'>;

const isHttpLink = (url: string): boolean => /^(https?:)?\/\//i.test(url);

/** Registers a URL and returns its 1-based footnote index, reusing duplicates. */
const registerLink = (ctx: InlineContext, href: string): number => {
  const existing = ctx.seen[href];
  if (existing) return existing;
  const index = ctx.links.length + 1;
  ctx.seen[href] = index;
  ctx.links.push({ index, href });
  return index;
};

const pushRun = (runs: PdfTextRun[], run: PdfTextRun): void => {
  if (!run.lineBreak && !run.text) return;
  const previous = runs[runs.length - 1];
  // Merge adjacent runs with identical styling to keep the layout pass cheap.
  if (
    previous &&
    !previous.lineBreak &&
    !run.lineBreak &&
    !previous.superscript &&
    !run.superscript &&
    !!previous.bold === !!run.bold &&
    !!previous.italic === !!run.italic &&
    !!previous.code === !!run.code &&
    !!previous.strike === !!run.strike &&
    previous.href === run.href
  ) {
    previous.text += run.text;
    return;
  }
  runs.push(run);
};

const inlineToRuns = (
  nodes: MdNode[] | undefined,
  ctx: InlineContext,
  style: RunStyle = {}
): PdfTextRun[] => {
  const runs: PdfTextRun[] = [];
  if (!nodes) return runs;

  const walk = (children: MdNode[] | undefined, current: RunStyle): void => {
    if (!children) return;

    children.forEach((node) => {
      switch (node.type) {
        case 'text':
          pushRun(runs, { ...current, text: sanitizeForPdf(node.value || '') });
          break;

        case 'inlineCode':
          pushRun(runs, { ...current, code: true, text: sanitizeForPdf(node.value || '') });
          break;

        case 'strong':
          walk(node.children, { ...current, bold: true });
          break;

        case 'emphasis':
          walk(node.children, { ...current, italic: true });
          break;

        case 'delete':
          walk(node.children, { ...current, strike: true });
          break;

        case 'link': {
          const href = node.url || '';
          walk(node.children, { ...current, href: href || undefined });
          if (href && isHttpLink(href) && ctx.linkFootnotes) {
            pushRun(runs, { text: String(registerLink(ctx, href)), superscript: true });
          }
          break;
        }

        case 'image': {
          // Remote images cannot be fetched synchronously (and are usually
          // cross-origin), so they degrade to a labelled link.
          const alt = sanitizeForPdf(node.alt || 'image');
          const href = node.url || '';
          pushRun(runs, { ...current, italic: true, text: `[${alt}]` });
          if (href && isHttpLink(href)) {
            pushRun(runs, { ...current, href, text: ` ${sanitizeForPdf(href)}` });
          }
          break;
        }

        case 'break':
          pushRun(runs, { text: '', lineBreak: true });
          break;

        case 'thematicBreak':
          break;

        case 'footnoteReference':
          break;

        case 'html':
          // Strip tags rather than dropping the node — chat answers occasionally
          // inline a <br> or a <sup>.
          pushRun(runs, {
            ...current,
            text: sanitizeForPdf((node.value || '').replace(/<[^>]*>/g, '')),
          });
          break;

        default:
          if (node.children) walk(node.children, current);
          else if (node.value) pushRun(runs, { ...current, text: sanitizeForPdf(node.value) });
          break;
      }
    });
  };

  walk(nodes, style);
  return runs;
};

const hasContent = (runs: PdfTextRun[]): boolean =>
  runs.some((run) => run.lineBreak || run.text.trim().length > 0);

const blocksFromNodes = (nodes: MdNode[] | undefined, ctx: InlineContext): PdfBlock[] => {
  const blocks: PdfBlock[] = [];
  if (!nodes) return blocks;

  nodes.forEach((node) => {
    switch (node.type) {
      case 'heading': {
        const runs = inlineToRuns(node.children, ctx);
        if (hasContent(runs)) {
          blocks.push({ type: 'heading', depth: node.depth || 1, runs });
        }
        break;
      }

      case 'paragraph': {
        const runs = inlineToRuns(node.children, ctx);
        if (hasContent(runs)) blocks.push({ type: 'paragraph', runs });
        break;
      }

      case 'list': {
        const items: PdfListItem[] = (node.children || []).map((item) => ({
          checked: item.checked === undefined ? null : item.checked,
          blocks: blocksFromNodes(item.children, ctx),
        }));
        if (items.length) {
          blocks.push({
            type: 'list',
            ordered: !!node.ordered,
            start: typeof node.start === 'number' && node.start !== null ? node.start : 1,
            items,
          });
        }
        break;
      }

      case 'code':
        blocks.push({
          type: 'code',
          value: sanitizeForPdf(node.value || ''),
          lang: node.lang || undefined,
        });
        break;

      case 'blockquote': {
        const inner = blocksFromNodes(node.children, ctx);
        if (inner.length) blocks.push({ type: 'blockquote', blocks: inner });
        break;
      }

      case 'table': {
        const rows = node.children || [];
        if (!rows.length) break;
        const toCells = (row: MdNode): PdfTableCell[] =>
          (row.children || []).map((cell) => ({ runs: inlineToRuns(cell.children, ctx) }));
        const header = toCells(rows[0]);
        const body = rows.slice(1).map(toCells);
        blocks.push({ type: 'table', header, rows: body });
        break;
      }

      case 'thematicBreak':
        blocks.push({ type: 'thematicBreak' });
        break;

      case 'html': {
        const stripped = sanitizeForPdf((node.value || '').replace(/<[^>]*>/g, '')).trim();
        if (stripped) blocks.push({ type: 'paragraph', runs: [{ text: stripped }] });
        break;
      }

      case 'listItem':
        blocks.push(...blocksFromNodes(node.children, ctx));
        break;

      default:
        if (node.children) blocks.push(...blocksFromNodes(node.children, ctx));
        break;
    }
  });

  return blocks;
};

/**
 * Parses GFM markdown into renderable blocks.
 *
 * `linkFootnotes` controls only the superscript markers and the returned link
 * list — inline links stay clickable either way.
 */
export const parseMarkdown = (markdown: string, linkFootnotes = true): MarkdownDocument => {
  const ctx: InlineContext = { links: [], linkFootnotes, seen: {} };
  if (!markdown || !markdown.trim()) return { blocks: [], links: [] };

  const tree = fromMarkdown(markdown, {
    extensions: [gfmSyntax()],
    mdastExtensions: [gfmFromMarkdown],
  }) as unknown as MdNode;

  return { blocks: blocksFromNodes(tree.children, ctx), links: ctx.links };
};

/**
 * Wraps plain text (user turns) in the same block model, preserving blank-line
 * paragraph breaks and single newlines.
 */
export const parsePlainText = (text: string): MarkdownDocument => {
  const clean = sanitizeForPdf(text || '');
  if (!clean.trim()) return { blocks: [], links: [] };

  const blocks: PdfBlock[] = clean
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => {
      const runs: PdfTextRun[] = [];
      paragraph.split('\n').forEach((line, index) => {
        if (index > 0) runs.push({ text: '', lineBreak: true });
        runs.push({ text: line });
      });
      return { type: 'paragraph', runs } as PdfBlock;
    });

  return { blocks, links: [] };
};
